import { FormHelperText } from "@mui/material";
import { useMprcTheme } from "components/MprcThemeProvider";
import { Delta, EmitterSource, MprcQuillContainer, Quill, QuillCommands, QuillEditorProps, QuillTranslateToolbar, useQuill } from "components/Quill";
import * as React from "react";
import { useEffect, useImperativeHandle } from "react";

///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

const MprcQuillEditorComponent: React.ForwardRefRenderFunction<QuillCommands, QuillEditorProps> = (props, forwardRef) => {

    const {
        children, initialValue, error, helperText, placeholder, formats, modules,
        onChange, onQuillReady, onRegisterModules, onUnmount, onFocus,
        theme = "snow",
        autoFocus = false,
        disabled = false,
    } = props;
    const { theme: mprcTheme } = useMprcTheme();

    const id = props.id || `ed_${new Date().getTime()}`;
    const q = React.useRef<Quill>(undefined!);
    const iv = React.useRef<string | null | undefined>(initialValue);

    const style: React.CSSProperties = {
        border: props.error ? "solid 1px red" : undefined,
        minHeight: props.minHeight || "auto",
        maxHeight: props.maxHeight || "auto",
        overflowY: props.maxHeight ? "auto" : undefined,
        marginBottom: props.marginBottom ? props.marginBottom : undefined,
    };

    //////////////////////////////////////////////
    const { quill, quillRef, Quill } = useQuill({ theme, modules, formats, placeholder });
    //////////////////////////////////////////////

    const focus = () => { quill?.focus(); };
    const disable = () => { quill?.disable(); };
    const clear = () => { if (q.current) q.current.root.innerHTML = ""; };
    const setValue = (value: string) => { if (q.current) q.current.root.innerHTML = value; };
    const getValueHtml = () => q.current?.root.innerHTML ?? "";
    const getValueText = () => q.current?.root.innerText == "\n" ? "" : (q.current?.root.innerText ?? "");
    const insert = (blotName: string, data?: any) => {
        const range = q.current.getSelection(true);
        if (range != null) {
            q.current.insertEmbed(range.index, blotName, data, "api");
            q.current.insertText(range.index + 1, ' ', "api");
            q.current.setSelection(range.index + 2, 1, "silent");
        }
    };
    const getQuillRef = () => q.current;

    useImperativeHandle(forwardRef, () => ({
        focus,
        disable,
        insert,
        clear,
        setValue,
        getQuillRef,
        onFocus,
        getValueHtml,
        getValueText
    }));

    //////////////////////////////////////////////

    if (Quill && !quill) {
        onRegisterModules && onRegisterModules(Quill);
    }

    const quillOnChangeHandle = React.useCallback((delta: Delta, oldDelta: Delta, source: EmitterSource) => {
        if (quill && quill.root.innerHTML != iv.current) {
            onChange?.(
                quill.root.innerText,
                delta,
                source,
                {
                    getBounds: quill.getBounds,
                    getLength: quill.getLength,
                    getText: (index?: number, length?: number) => quill.getText(index, length),
                    getHTML: () => quill.root.innerHTML,
                    getSelection: quill.getSelection,
                    getContents: quill.getContents
                });
        }
    }, [quill, iv.current]);

    const monitoringTextChange = (active: boolean) => {
        if (quill) {
            if (active) quill.on('text-change', quillOnChangeHandle);
            else quill.off('text-change', quillOnChangeHandle);
        }
    };

    const monitoringFocus = React.useCallback((active: boolean) => {
        if (quill && onFocus) {
            var elementChild = document.getElementById(id)?.firstChild;
            if (elementChild) {

                if (active) {
                    elementChild.addEventListener("focus", () => onFocus(true));
                    elementChild.addEventListener("blur", () => onFocus(false));
                } else {
                    elementChild.removeEventListener("focus", () => onFocus(true));
                    elementChild.removeEventListener("blur", () => onFocus(false));
                }
            }
        }
    }, [quill, onFocus, id]);

    useEffect(() => {
        if (quill) {
            q.current = quill;

            onQuillReady && onQuillReady();

            var qtr = new QuillTranslateToolbar(id, mprcTheme);
            qtr.translate();

            if (autoFocus) quill.focus();
            if (disabled) quill.disable();
            if (initialValue) quill.clipboard.dangerouslyPasteHTML(0, initialValue, "silent");
            monitoringTextChange(true);
            monitoringFocus(true);

        }
        return () => {
            onUnmount && onUnmount();
            monitoringTextChange(false);
            monitoringFocus(false);
        };
    }, [quill]);


    useEffect(() => {
        if (quill && iv.current !== initialValue) {
            // Solução adaptada de 'https://github.com/quill-mention/quill-mention/issues/174' para remover
            // o problema de duplicação da tag "<span contenteditable='false>" quando repopulando
            // um conteúdo que contenha menções.
            monitoringTextChange(false);
            quill.root.innerHTML = "";
            quill.clipboard.dangerouslyPasteHTML(0, initialValue ?? "", "silent");
            iv.current = initialValue;
            monitoringTextChange(true);
        }
    }, [initialValue]);

    return (
        <>
            {children}
            <MprcQuillContainer spellCheck={false} style={style} showToolbar={props.toolbar ?? true} >
                <div id={id} ref={quillRef} />
                {error && helperText && <FormHelperText error>{helperText}</FormHelperText>}
            </MprcQuillContainer>
        </>
    );
};

export const MprcQuillEditor = React.forwardRef(MprcQuillEditorComponent);
